/* Email a quote: who, subject, the message (prefilled from Settings), a live preview of exactly what leaves, Send.
 * Through Gmail when it is connected (the email sits in Sent, replies come back to the inbox and show in the app);
 * otherwise Shopify's own invoice email with the message as its custom text. */
import { useEffect, useMemo, useState } from 'preact/hooks';
import { useLocation, useRoute } from 'preact-iso';
import { getQuote, listOptions, markSent, sendShopifyInvoice } from '../lib/admin.js';
import { quoteNumber } from '../lib/quote.js';
import { defaultMessage, fill, renderQuoteEmail, vars } from '../lib/email.js';
import { draftGid, idOf, useApp } from '../lib/app-state.js';
import { busy, sendMail, toast } from '../lib/server.js';

export default function SendPage() {
  const app = useApp();
  const { params } = useRoute();
  const { route } = useLocation();
  const [q, setQ] = useState(null);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [payButton, setPayButton] = useState(true);
  const [sending, setSending] = useState(false);
  const [others, setOthers] = useState([]); // the quote's other options, loaded in full
  const [together, setTogether] = useState(true);
  const gmail = app.config?.gmail?.connected;
  const from = app.config?.gmail?.email || app.shop.contactEmail;

  useEffect(() => {
    getQuote(draftGid(params.id))
      .then((r) => {
        setQ(r);
        setTo(r.email || r.customer?.email || '');
        const again = !!r.sentAt;
        setSubject(again && r.sent?.subject ? `Re: ${r.sent.subject.replace(/^Re:\s*/i, '')}` : fill(app.settings.subject, vars(r)));
        setMessage(again ? fill(app.settings.followUp, vars(r)) + fill(app.settings.closing, vars(r)) : defaultMessage(r, app.settings));
        if (r.group) {
          listOptions(r.group)
            .then((os) => Promise.all(os.filter((o) => o.id !== r.id && o.status !== 'COMPLETED').map((o) => getQuote(o.id))))
            .then((qs) => {
              setOthers(qs);
              if (qs.length && !again) {
                setMessage((m) => m
                  .replace(/(\n\n[^\n]*$)/, `\n\nThere are ${qs.length + 1} options below — each has its own pay button, so just pick the one that suits you.$1`)
                  .replace('with the button below', 'with the buttons below'));
              }
            })
            .catch(() => setOthers([]));
        }
      })
      .catch((e) => toast(e.message, true));
  }, [params.id]);

  const all = q ? (together && others.length ? [q, ...others].sort((a, b) => a.name.localeCompare(b.name, 'en', { numeric: true })) : [q]) : [];
  const email = useMemo(
    () => (q ? renderQuoteEmail(all, { message, subject, settings: app.settings, payButton, shop: { address: shopAddress(app.shop) } }) : null),
    [q, others, together, message, subject, payButton, app.settings],
  );

  if (!q) {
    return (
      <s-page heading="Email quote">
        <s-section><s-stack alignItems="center" padding="large"><s-spinner accessibilityLabel="Loading" size="large" /></s-stack></s-section>
      </s-page>
    );
  }

  /* A test: the same email to an address of your own, "[Test]" in the subject, the quote left unsent. */
  const [testTo, setTestTo] = useState(() => {
    try { return localStorage.getItem('afxq-test-to') || ''; } catch { return ''; }
  });
  const [testing, setTesting] = useState(false);
  const sendTest = async () => {
    const t = testTo.trim();
    if (!t) return toast('Type the address for the test', true);
    if (!gmail) return toast('Tests go through Gmail — connect it in Settings', true);
    setTesting(true);
    try {
      await sendMail({ to: t, subject: `[Test] ${email.subject}`, text: email.text, html: email.html });
      try { localStorage.setItem('afxq-test-to', t); } catch { /* */ }
      toast(`Test sent to ${t}`);
    } catch (e) {
      toast(e.message, true);
    } finally {
      setTesting(false);
    }
  };

  const send = async () => {
    if (!to.trim()) return toast('Add the customer’s email', true);
    setSending(true);
    busy(true);
    try {
      let info;
      if (gmail) {
        const r = await sendMail({
          to: to.trim(), cc: cc.trim(), subject: email.subject, text: email.text, html: email.html,
          threadId: q.sent?.threadId || undefined,
        });
        info = { at: r.at, to: to.trim(), via: 'gmail', from: r.from, subject: email.subject, threadId: r.threadId, messageId: r.id };
      } else {
        for (const x of all) await sendShopifyInvoice(x.id, { to: to.trim(), subject: email.subject, customMessage: message });
        info = { at: new Date().toISOString(), to: to.trim(), via: 'shopify', subject: email.subject };
      }
      await Promise.all(all.map((x) => markSent(x.id, { ...info, with: all.map((y) => y.name) }).catch(() => {})));
      toast(all.length > 1 ? `${all.length} options sent to ${to.trim()}` : `Quote ${quoteNumber(q)} sent to ${to.trim()}`);
      route(`/quotes/${idOf(q.id)}`);
    } catch (e) {
      toast(e.message, true);
    } finally {
      setSending(false);
      busy(false);
    }
  };

  return (
    <s-page heading={`Email quote ${quoteNumber(q)}`} inlineSize="base">
      <s-link slot="breadcrumb-actions" href={`/quotes/${idOf(q.id)}`}>Quote {quoteNumber(q)}</s-link>
      <s-button slot="primary-action" variant="primary" loading={sending} onClick={send}>Send</s-button>

      {!gmail && (
        <s-banner tone="warning" heading="Gmail is not connected">
          <s-paragraph>This will go out as Shopify’s draft-order invoice email, with your message at the top. Connect Gmail in Settings to send from your own mailbox instead.</s-paragraph>
        </s-banner>
      )}

      <s-section>
        <s-stack gap="base">
          <s-text color="subdued">From {app.settings.fromName} &lt;{from}&gt;{gmail ? ' via Gmail' : ' via Shopify'}</s-text>
          <s-grid gridTemplateColumns="1fr auto" gap="small" alignItems="end">
            <s-email-field label="To" value={to} onInput={(e) => setTo(e.currentTarget.value)} />
            {gmail && !showCc && <s-button variant="tertiary" onClick={() => setShowCc(true)}>Cc</s-button>}
          </s-grid>
          {showCc && <s-text-field label="Cc" placeholder="Separate several with commas" value={cc} onInput={(e) => setCc(e.currentTarget.value)} />}
          <s-text-field label="Subject" value={subject} onInput={(e) => setSubject(e.currentTarget.value)} />
          <s-text-area label="Message" rows={7} value={message} onInput={(e) => setMessage(e.currentTarget.value)} details="The quote table, totals and your signature are added below it automatically." />
          {others.length > 0 && (
            <s-checkbox
              label={`Send all ${others.length + 1} options in this email`}
              details={[q, ...others].map((x) => `${x.name}${x.option ? ` (${x.option})` : ''}`).join(' · ')}
              checked={together}
              onChange={(e) => setTogether(e.currentTarget.checked)}
            />
          )}
          <s-checkbox
            label="Include a “Review & pay online” button"
            details="Opens your store’s secure checkout with this quote already in it."
            checked={payButton}
            onChange={(e) => setPayButton(e.currentTarget.checked)}
          />
        </s-stack>
      </s-section>

      {gmail && (
        <s-section heading="Send a test first">
          <s-grid gridTemplateColumns="1fr auto" gap="small" alignItems="end">
            <s-email-field label="Test address" placeholder="you@example.com" value={testTo} onInput={(e) => setTestTo(e.currentTarget.value)}
              details="Gets exactly this email with [Test] in the subject. The quote is not marked as sent." />
            <s-button loading={testing} onClick={sendTest}>Send test</s-button>
          </s-grid>
        </s-section>
      )}

      <s-section heading="Preview">
        {gmail ? (
          <iframe class="preview-frame" title="Email preview" sandbox="" srcdoc={email.html} />
        ) : (
          <s-paragraph color="subdued">Shopify’s invoice template decides how the email looks; your message appears at the top of it.</s-paragraph>
        )}
      </s-section>
    </s-page>
  );
}

export function shopAddress(shop) {
  const a = shop?.billingAddress;
  if (!a) return '';
  return [shop.name, a.address1, [a.city, a.provinceCode, a.zip].filter(Boolean).join(' '), a.phone].filter(Boolean).join(' · ');
}
